import { Menu, Plus, Search } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import CollapsibleFilters from '../../../components/CollapsibleFilters';
import ManagerBottomNavigationBar from '../../../components/ManagerBottomNavigationBar';
import ManagerTaskCard from '../../../components/ManagerTaskCard';
import SidebarMenu from '../../../components/SidebarMenu';
import UserDropdown from '../../../components/UserDropdown';
import WeekSelector from '../../../components/WeekSelector';
import type { User, RecurrenceTaskStatus } from '../../../service/api';
import useManagerTasksStore, {
  type ManagerTaskItem,
} from '../../../store/managerTasks';

interface UserTaskInfo {
  user: User;
  taskId: string;
  status: RecurrenceTaskStatus;
}

interface TaskWithUsers {
  task: ManagerTaskItem;
  userTasks: UserTaskInfo[];
}

interface GroupedTask {
  date: DateTime;
  tasks: TaskWithUsers[];
}

export default function ManagerTasksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  const [titleFilter, setTitleFilter] = useState('');
  const todaySectionRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  const {
    userTasks,
    selectedUserId,
    weekStart,
    isLoading,
    error,
    fetchTasks,
    setSelectedUser,
    setWeek,
  } = useManagerTasksStore();

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Auto-scroll to Today section when opened from bottom navigation
  useEffect(() => {
    const isFromBottomNav = location.state?.from === 'bottom-navigation';

    if (
      isFromBottomNav &&
      !isLoading &&
      todaySectionRef.current &&
      stickyHeaderRef.current
    ) {
      // Small delay to ensure content is rendered
      const timeoutId = setTimeout(() => {
        const stickyHeaderHeight = stickyHeaderRef.current?.offsetHeight || 0;
        const todaySection = todaySectionRef.current;

        if (todaySection) {
          const elementPosition = todaySection.offsetTop;
          const offsetPosition = elementPosition - stickyHeaderHeight - 16; // 16px extra padding

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, location.state]);

  const filteredUserTasks = selectedUserId
    ? userTasks.filter(ut => ut.user._id === selectedUserId)
    : userTasks;

  // Group tasks by recurrence ID to avoid duplicates
  const tasksMap = new Map<string, TaskWithUsers>();

  filteredUserTasks.forEach(({ user, tasks }) => {
    tasks.forEach(task => {
      const key = `${task.recurrenceId}-${task.start.toISO()}-${task.stop.toISO()}`;
      
      if (tasksMap.has(key)) {
        // Add user task info to existing task
        const existing = tasksMap.get(key)!;
        if (!existing.userTasks.find(ut => ut.user._id === user._id)) {
          existing.userTasks.push({
            user,
            taskId: task._id,
            status: task.status
          });
        }
      } else {
        // Create new task entry
        tasksMap.set(key, {
          task,
          userTasks: [{
            user,
            taskId: task._id,
            status: task.status
          }]
        });
      }
    });
  });

  const allTasks = Array.from(tasksMap.values())
    .sort((a, b) => a.task.start.toMillis() - b.task.start.toMillis());

  // Apply title filter
  const filteredTasks = titleFilter.trim()
    ? allTasks.filter(({ task }) =>
        task.title.toLowerCase().includes(titleFilter.toLowerCase().trim()),
      )
    : allTasks;

  const groupedByDay: GroupedTask[] = [];
  filteredTasks.forEach(taskWithUsers => {
    const taskDate = taskWithUsers.task.start.startOf('day');
    const existingGroup = groupedByDay.find(group =>
      group.date.hasSame(taskDate, 'day'),
    );

    if (existingGroup) {
      existingGroup.tasks.push(taskWithUsers);
    } else {
      groupedByDay.push({
        date: taskDate,
        tasks: [taskWithUsers],
      });
    }
  });

  const formatDayHeader = (date: DateTime) => {
    const today = DateTime.now().startOf('day');
    const tomorrow = today.plus({ days: 1 });

    if (date.hasSame(today, 'day')) {
      return 'Today';
    } else if (date.hasSame(tomorrow, 'day')) {
      return 'Tomorrow';
    } else {
      return date.toFormat('EEEE, MMMM d');
    }
  };

  const isToday = (date: DateTime) => {
    const today = DateTime.now().startOf('day');
    return date.hasSame(today, 'day');
  };

  return (
    <CollapsibleFilters.Provider>
      <div className="bg-white min-vh-100 d-flex flex-column">
        <Container fluid className="flex-grow-1 pb-5">
          {/* Sticky Header */}
          <div
            ref={stickyHeaderRef}
            className="sticky-top bg-white"
            style={{
              zIndex: 1020,
              borderBottom: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            {/* Title Bar */}
            <Row className="align-items-center p-4 pb-2">
              <Col xs="auto">
                <Menu
                  size={24}
                  className="text-dark"
                  onClick={() => setShowSidebar(true)}
                  style={{ cursor: 'pointer' }}
                />
              </Col>
              <Col className="d-flex flex-row align-items-center justify-content-center">
                <h2 className="text-dark h5 fw-bold mb-0 me-3">
                  {t('manager.tasks.title')}
                </h2>
                <CollapsibleFilters.Button />
              </Col>
              <Col xs="auto">
                <Button
                  variant="primary"
                  size="sm"
                  className="rounded-circle p-2"
                  onClick={() => navigate('/manager/create-task')}
                >
                  <Plus size={20} />
                </Button>
              </Col>
            </Row>

            {/* Collapsible Filters Content */}
            <CollapsibleFilters.Content>
              <Row className="align-items-center g-3">
                <Col xs={12} sm="auto">
                  <h6 className="mb-0 fw-bold">{t('manager.tasks.filters')}</h6>
                </Col>
                <Col xs={5} sm="auto">
                  <UserDropdown
                    selectedUserId={selectedUserId}
                    onUserSelect={setSelectedUser}
                  />
                </Col>
                <Col xs={7} sm="auto">
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-end-0">
                      <Search size={16} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t('manager.tasks.searchByTitle')}
                      value={titleFilter}
                      onChange={e => setTitleFilter(e.target.value)}
                      className="border-start-0"
                      style={{ fontSize: '14px' }}
                    />
                  </InputGroup>
                </Col>
                <Col xs={12} sm>
                  <WeekSelector weekStart={weekStart} onWeekChange={setWeek} />
                </Col>
              </Row>
            </CollapsibleFilters.Content>
          </div>

          {/* Content */}
          <div className="px-0 pt-3">
            {isLoading && (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {!isLoading && !error && filteredTasks.length === 0 && (
              <p className="text-center text-muted py-4">
                {titleFilter.trim()
                  ? t('manager.tasks.noTasksFound')
                  : t('manager.tasks.noRequests')}
              </p>
            )}

            {!isLoading &&
              !error &&
              groupedByDay.map(({ date, tasks }) => (
                <div
                  key={date.toISO()}
                  className="mb-4"
                  ref={isToday(date) ? todaySectionRef : null}
                >
                  <h6 className="fw-bold text-muted mb-3">
                    {formatDayHeader(date)}
                  </h6>
                  {tasks.map(({ task, userTasks }) => (
                    <ManagerTaskCard 
                      key={`${task.recurrenceId}-${task.start.toISO()}-${task.stop.toISO()}`}
                      task={task} 
                      userTasks={userTasks} 
                    />
                  ))}
                </div>
              ))}
          </div>
        </Container>

        <SidebarMenu show={showSidebar} onHide={() => setShowSidebar(false)} />
        <ManagerBottomNavigationBar />
      </div>
    </CollapsibleFilters.Provider>
  );
}
